type CreationContextContract = {
	/** Fields this experience must never send. */
	omit: readonly string[];
	/**
	 * Fields that can identify the remix-edit target for backend validation. These are not
	 * mutually exclusive: a style update can carry both a generated media id and the selected
	 * subtype.
	 */
	targets?: readonly string[];
	/**
	 * When set, `blocks` is always sent and constrained to these values, taken from the block
	 * actually being edited (`dynamicUiType`) in preference to whatever the call site declared.
	 */
	blocks?: readonly string[];
	/**
	 * Fields where at most one must be sent. The first present field wins, and the rest are
	 * dropped so the content reference is unambiguous.
	 */
	exclusiveOneOf?: readonly string[];
};

/**
 * Per-experience creation context contracts.
 *
 * Add a row as each experience is migrated. An experience with no row here is passed through
 * untouched, so this is safe to extend one experience at a time.
 */
const CREATION_CONTEXT_CONTRACTS: Record<string, CreationContextContract> = {
	remix_edit: {
		omit: ['contentTypes'],
		// A remix edit can target existing content, or a subtype-only style update.
		targets: ['contentMauiId', 'mediaFileId', 'dynamicUiSubtype'],
		exclusiveOneOf: ['contentMauiId', 'mediaFileId'],
		// 'image' is not in the published contract table, but image remixes are a real block type
		// and must not be reported as one of the others.
		blocks: ['chart', 'diagram', 'visual-aid', 'infographic', 'image'],
	},
};

/**
 * Applies the creation context contract for the experience being sent.
 *
 * Every entry point funnels through `populateSendMessageContext`, so contracts are enforced here
 * once rather than at each call site.
 */
export function applyCreationContextContract(
	creationContext: Record<string, unknown>,
): Record<string, unknown> {
	const experience = creationContext.experience;
	const contract =
		typeof experience === 'string' ? CREATION_CONTEXT_CONTRACTS[experience] : undefined;

	if (!contract) {
		return creationContext;
	}

	const result: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(creationContext)) {
		if (!contract.omit.includes(key)) {
			result[key] = value;
		}
	}

	if (contract.targets) {
		for (const key of contract.targets) {
			if (result[key] === undefined || result[key] === '') {
				delete result[key];
			}
		}
	}

	if (contract.exclusiveOneOf) {
		const [kept] = contract.exclusiveOneOf.filter(
			(key) => result[key] !== undefined && result[key] !== '',
		);
		for (const key of contract.exclusiveOneOf) {
			if (key !== kept) {
				delete result[key];
			}
		}
	}

	if (contract.blocks) {
		const allowed = contract.blocks;
		const isAllowed = (value: unknown): value is string =>
			typeof value === 'string' && allowed.includes(value);

		// The block being edited is authoritative: a call site can derive `blocks` from the
		// presence of a media id, which mislabels a visual aid as an image.
		const declared = Array.isArray(result.blocks) ? result.blocks.filter(isAllowed) : [];
		const resolved = isAllowed(result.dynamicUiType)
			? [result.dynamicUiType]
			: declared.length > 0
				? declared
				: undefined;

		if (resolved) {
			result.blocks = resolved;
		} else {
			delete result.blocks;
		}
	}

	return result;
}
